import React from 'react';
import _ from 'lodash';

import { useReturnAuthorizationGroupTableQuery } from 'generated/graphql';
import { NetSuiteRecordChip } from 'modules';
import { DateTime } from 'luxon';
import * as types from './types';



export interface IReturnAuthorizationGroup {
  id: string;
  createdTimestamp: DateTime;
  storeName: string;
  referenceNumber: string;
  totalReturns: number;
  returnAuthorizations: types.IReturnAuthorization[];
}

export interface IData {
  groups: IReturnAuthorizationGroup[];
}

export function useQuery(initialFilter: types.IFilter) {
  const tuple = useReturnAuthorizationGroupTableQuery({
    variables: {
      filter: {
        storeId: initialFilter.storeId,
        inState: 'preAlert',
        omniFreeText: initialFilter.searchText
      }
    }
  });

  const data = React.useMemo(
    (): IData => {
      const groupId2Returns = _.groupBy(tuple.data?.tier1ReturnAuthorizations.nodes, node => node.group.id);
      const groupId2Data = _.mapValues(groupId2Returns, returns => {
        const gqlGroup = returns[0].group;

        const group: IReturnAuthorizationGroup = {
          id: gqlGroup.id,
          createdTimestamp: DateTime.fromISO(gqlGroup.createdTimestamp),
          storeName: gqlGroup.store.name,
          referenceNumber: gqlGroup.referenceNumber,
          totalReturns: gqlGroup.totalReturnCount,
          returnAuthorizations: returns.map((rma): types.IReturnAuthorization => ({
            id: rma.id,
            netSuiteRecord: NetSuiteRecordChip.genWriteStatus({
              netSuiteRecord: rma.netSuiteRecord,
              writeRequest: rma.netSuiteWriteRequest
            }),
            itemId: rma.item.id,
            itemName: rma.item.name,
            itemSku: rma.item.sku,
            preAlertDate: DateTime.fromISO(rma.group.createdTimestamp),
            storeName: gqlGroup.store.name,
            referenceNumber: gqlGroup.referenceNumber,
            expectedSerialNumber: rma.expectedSerialNumber,
            netSuiteTransactionId: rma.netSuiteRecord?.transactionId ?? '-',
            storeId: rma.group.store.id
          }))
        };

        return group;
      });

      return {
        groups: _.flatten(_.values(groupId2Data))
      };
    },
    [tuple.data?.tier1ReturnAuthorizations.nodes]
  );

  return React.useMemo(
    () => ({
      ...tuple,
      data
    }),
    [data, tuple]
  );
}
